/**
 * Error codes and the rules around them, with no dependency of their own: a
 * client imports them through `@sunucupark/shared/lite` without pulling zod and
 * every contract into its first download (ADR 0006, bundle budget).
 */

/**
 * Machine-readable error codes. The gateway always answers errors with the
 * `ApiErrorSchema` envelope; the app maps codes to localized messages.
 */
export const ErrorCode = {
  // generic
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  UNSUPPORTED_MEDIA_TYPE: 'UNSUPPORTED_MEDIA_TYPE',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  // auth
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  ACCESS_TOKEN_EXPIRED: 'ACCESS_TOKEN_EXPIRED',
  REFRESH_TOKEN_INVALID: 'REFRESH_TOKEN_INVALID',
  REFRESH_TOKEN_REUSED: 'REFRESH_TOKEN_REUSED',
  SESSION_REVOKED: 'SESSION_REVOKED',
  /** The session ended by itself — idle for too long or past its absolute lifetime (browser sessions); sign in again. */
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  /** Too many failed sign-ins for this account, or from this address for it; wait `retryAfter` seconds. */
  TOO_MANY_ATTEMPTS: 'TOO_MANY_ATTEMPTS',
  /** A browser sign-in route was called from an origin that is not allowed, or without `X-Requested-With: fetch`. */
  ORIGIN_NOT_ALLOWED: 'ORIGIN_NOT_ALLOWED',
  TENANT_NOT_FOUND: 'TENANT_NOT_FOUND',
  ACCOUNT_DISABLED: 'ACCOUNT_DISABLED',
  /** details: `{ reason }` (PasswordChangeRequiredDetailsSchema, auth.ts). */
  PASSWORD_CHANGE_REQUIRED: 'PASSWORD_CHANGE_REQUIRED',
  /** The expired-password change was asked for an account the mail server is not holding back: sign in as usual. */
  PASSWORD_CHANGE_NOT_REQUIRED: 'PASSWORD_CHANGE_NOT_REQUIRED',
  // two-step sign-in (two-factor.ts)
  /** The code is wrong; details: `{ attemptsLeft }` (TwoFactorCodeInvalidDetailsSchema). */
  TWO_FACTOR_CODE_INVALID: 'TWO_FACTOR_CODE_INVALID',
  /** The challenge or setup is unknown, used, timed out or out of attempts: start the step again. */
  TWO_FACTOR_EXPIRED: 'TWO_FACTOR_EXPIRED',
  /** This server cannot offer the second step (or the chosen method) for the account right now: switched off, no SMS provider, no access to its number. */
  TWO_FACTOR_UNAVAILABLE: 'TWO_FACTOR_UNAVAILABLE',
  /** details: `{ problem }` (PhoneNumberInvalidDetailsSchema): not a number, a country we cannot send to, not a mobile. */
  PHONE_NUMBER_INVALID: 'PHONE_NUMBER_INVALID',
  /** The SMS step is on, but the account has no mobile number recorded any more: codes cannot be sent. */
  PHONE_NUMBER_MISSING: 'PHONE_NUMBER_MISSING',
  /** The SMS provider did not take the message; `retryAfter` when it said when to try again. */
  SMS_SEND_FAILED: 'SMS_SEND_FAILED',
  // password change
  /** details: `{ rule, value? }` (PasswordPolicyViolationDetailsSchema, auth.ts). */
  PASSWORD_POLICY_VIOLATION: 'PASSWORD_POLICY_VIOLATION',
  PASSWORD_RECENTLY_USED: 'PASSWORD_RECENTLY_USED',
  PASSWORD_CHANGE_TOO_SOON: 'PASSWORD_CHANGE_TOO_SOON',
  PASSWORD_CHANGE_NOT_ALLOWED: 'PASSWORD_CHANGE_NOT_ALLOWED',
  PASSWORD_LOCKED: 'PASSWORD_LOCKED',
  // devices
  DEVICE_NOT_FOUND: 'DEVICE_NOT_FOUND',
  // mail server (neutral names: clients never learn which server the gateway talks to)
  /** The mail server is not reachable or failing fast; `retryAfter` says when to try again. */
  MAIL_SERVER_UNAVAILABLE: 'MAIL_SERVER_UNAVAILABLE',
  /** The mail server failed the request for a reason with no code of its own. */
  MAIL_SERVER_ERROR: 'MAIL_SERVER_ERROR',
  /** The gateway's session with the mail server expired and could not be renewed. */
  MAIL_SERVER_AUTH_EXPIRED: 'MAIL_SERVER_AUTH_EXPIRED',
  MAIL_SERVER_PERMISSION_DENIED: 'MAIL_SERVER_PERMISSION_DENIED',
  MAIL_SERVER_QUOTA_EXCEEDED: 'MAIL_SERVER_QUOTA_EXCEEDED',
  // mail
  /** A single file is over the account's upload limit; `details.limitBytes` holds it when known. */
  ATTACHMENT_TOO_LARGE: 'ATTACHMENT_TOO_LARGE',
  /** The whole message is over the server's size limit; `details.limitBytes` is the attachment total it allows. */
  MESSAGE_TOO_LARGE: 'MESSAGE_TOO_LARGE',
  SEND_FAILED: 'SEND_FAILED',
  INVALID_RECIPIENT: 'INVALID_RECIPIENT',
  UPLOAD_EXPIRED: 'UPLOAD_EXPIRED',
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

/** HTTP status the gateway uses for each code; the app never depends on status alone. */
export const ERROR_HTTP_STATUS: Record<ErrorCode, number> = {
  VALIDATION_ERROR: 400,
  NOT_FOUND: 404,
  CONFLICT: 409,
  RATE_LIMITED: 429,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
  INTERNAL_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  INVALID_CREDENTIALS: 401,
  ACCESS_TOKEN_EXPIRED: 401,
  REFRESH_TOKEN_INVALID: 401,
  REFRESH_TOKEN_REUSED: 401,
  SESSION_REVOKED: 401,
  SESSION_EXPIRED: 401,
  TOO_MANY_ATTEMPTS: 429,
  ORIGIN_NOT_ALLOWED: 403,
  TENANT_NOT_FOUND: 404,
  ACCOUNT_DISABLED: 403,
  PASSWORD_CHANGE_REQUIRED: 403,
  PASSWORD_CHANGE_NOT_REQUIRED: 409,
  TWO_FACTOR_CODE_INVALID: 400,
  TWO_FACTOR_EXPIRED: 410,
  TWO_FACTOR_UNAVAILABLE: 503,
  PHONE_NUMBER_INVALID: 400,
  PHONE_NUMBER_MISSING: 409,
  SMS_SEND_FAILED: 502,
  PASSWORD_POLICY_VIOLATION: 400,
  PASSWORD_RECENTLY_USED: 400,
  PASSWORD_CHANGE_TOO_SOON: 403,
  PASSWORD_CHANGE_NOT_ALLOWED: 403,
  PASSWORD_LOCKED: 403,
  DEVICE_NOT_FOUND: 404,
  MAIL_SERVER_UNAVAILABLE: 503,
  MAIL_SERVER_ERROR: 502,
  MAIL_SERVER_AUTH_EXPIRED: 401,
  MAIL_SERVER_PERMISSION_DENIED: 403,
  MAIL_SERVER_QUOTA_EXCEEDED: 507,
  ATTACHMENT_TOO_LARGE: 413,
  MESSAGE_TOO_LARGE: 413,
  SEND_FAILED: 502,
  INVALID_RECIPIENT: 400,
  UPLOAD_EXPIRED: 410,
};

/** Codes for which the app should transparently refresh the access token and retry once. */
export const RETRY_AFTER_REFRESH_CODES: readonly ErrorCode[] = [
  ErrorCode.ACCESS_TOKEN_EXPIRED,
  ErrorCode.MAIL_SERVER_AUTH_EXPIRED,
];

/** Codes that terminate the local session (the user must sign in again). */
export const SESSION_TERMINATING_CODES: readonly ErrorCode[] = [
  ErrorCode.REFRESH_TOKEN_INVALID,
  ErrorCode.REFRESH_TOKEN_REUSED,
  ErrorCode.SESSION_REVOKED,
  ErrorCode.SESSION_EXPIRED,
  ErrorCode.ACCOUNT_DISABLED,
];
